import React, { useState, useEffect } from 'react';
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebaseConfig';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"; // Necesario para subir el QR
import Accordion from '../components/Accordion';
import { useUser } from '../contexts/UserContext';
// Usamos el nombre correcto del componente que me diste
import UploadQRModal from '../components/UploadQRModal';
import '../assets/styles/Accordion.css';
import VideoExplicativo from '../components/VideoExplicativo';

const TUTORIAL_VIDEOS = [
  {
    title: 'Apertura Cuenta Buda',
    description: 'Aprende el proceso paso a paso de cómo crear tu primera cuenta en Buda para empezar a operar.',
    videoId: 'SHsFiCA_4DA',
  },
  {
    title: 'Activación Cuenta Buda',
    description: 'Te guiamos en la configuración inicial de tu bot de trading automatizado para que empieces a operar en minutos.',
    videoId: 'loHb8lLHnW0',
  },
  {
    title: 'Apertura Cuenta Binance',
    description: 'Nuestro experto principal explica las estrategias básicas y avanzadas que cubrimos en nuestras asesorías privadas.',
    videoId: 'GR0UYsCN8ug',
  },
  {
    title: 'Agregar Saldo a Buda y comprar USDT',
    description: 'Aprende a cargar saldo en tu cuenta y convertirlo a USDT.',
    videoId: 'VHAea6S0mQI',
  },
  {
    title: 'Enviar USDT de Buda a Binance',
    description: 'Pasos para transferir tus fondos de manera segura entre exchanges.',
    videoId: '3HXdIQpkgIs',
  },
  {
    title: 'Activar Futuros en Binance',
    description: 'Habilita la opción de futuros en tu cuenta de Binance para el trading automatizado.',
    videoId: 'klW6WP10m-k',
  },
  {
    title: 'Traspasar dinero de Spot a Futuros en Binance',
    description: 'Mueve tus fondos a la billetera correcta para que el bot pueda operar.',
    videoId: '_G3KrYzZuAQ',
  },
  {
    title: 'Crear API en Binance y Almacenar QR',
    description: 'Genera las claves API necesarias y sube el código QR para conectar el servicio.',
    videoId: 'CvGvVjmWHKE',
  }
];

const TradingAutomatizadoView = () => {
  const { userData } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showQrWindow, setShowQrWindow] = useState(false);

  const [balanceData, setBalanceData] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  useEffect(() => {
    fetchBinanceBalance();
  }, []);

  const fetchBinanceBalance = async () => {
    setLoadingBalance(true);
    try {
      const fn = httpsCallable(functions, 'obtenerSaldoBinance');
      const result = await fn();
      const data = result.data;
      if (data.success) {
        setBalanceData(data);
      }
      // Si success: false (usuario sin claves) → No hacer nada.
      // Es comportamiento esperado, no un error que deba loguearse.
      // El UI ya muestra "Cuenta no vinculada" cuando balanceData es null.
    } catch (err) {
      // Solo loguear errores REALES (excepciones de red, auth, etc.)
      console.error("Error calling obtenerSaldoBinance:", err);
    } finally {
      setLoadingBalance(false);
    }
  };

  // --- LÓGICA PARA GUARDAR EL QR (Necesaria porque el modal llama a onSave) ---
  const handleSaveQr = async (profileData, qrFile) => {
    setIsLoading(true);
    setError(null);
    try {
      let qrUrl = userData?.qrCodeUrl || null;

      // 1. Si hay archivo, lo subimos a Storage
      if (qrFile) {
        const storage = getStorage();
        // Creamos una referencia única para el archivo
        const storageRef = ref(storage, `qr_codes/${userData.uid}/${Date.now()}_${qrFile.name}`);
        await uploadBytes(storageRef, qrFile);
        qrUrl = await getDownloadURL(storageRef);
      }

      // 2. Guardamos la URL en la base de datos usando tu función existente
      const gestionarPerfil = httpsCallable(functions, 'gestionarPerfilDeUsuario');
      await gestionarPerfil({
        qrCodeUrl: qrUrl
      });

      alert("¡Código QR subido y activado correctamente!");
      setShowQrWindow(false);

    } catch (err) {
      console.error("Error al subir QR:", err);
      setError("Hubo un error al subir el código QR. Inténtalo de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };
  // ---------------------------------------------------------------------------

  const handleSolicitarInforme = async () => {
    setIsLoading(true);
    setError(null);
    if (!userData?.email) {
      alert("Error: No se ha detectado un email en tu perfil.");
      setIsLoading(false);
      return;
    }

    try {
      // IMPORTANTE: Usa ruta relativa /api/ que Nginx redirige a puerto 5000
      const response = await fetch('/api/generar-informe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: userData.email }),
      });

      const data = await response.json();

      if (data.success) {
        alert(`¡Solicitud enviada! El informe se enviará a ${userData.email} en breves momentos.`);
      } else {
        // --- CUSTOM ERROR HANDLING ---
        if (data.message === "Cliente no encontrado") {
          throw new Error("Usted no es cliente activo");
        }
        throw new Error(data.message || "La API retornó un error.");
      }

    } catch (err) {
      console.error("Error al solicitar informe:", err);
      // If the error message is our custom one (or from backend), show it.
      // Otherwise, assume network error.
      if (err.message === "Usted no es cliente activo") {
        setError("Usted no es cliente activo");
      } else {
        setError("No se pudo conectar con el servidor de informes. Verifica tu conexión o intenta más tarde.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dashboard-page-content" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>

      {/* ========== SECCIÓN 1: PARA CLIENTES NUEVOS ========== */}
      <div className="seccion-clientes-nuevos" style={{ marginBottom: '50px' }}>
        <h2 style={{
          marginBottom: '30px',
          color: 'var(--text-color, #261f87)',
          fontSize: '1.8rem',
          fontWeight: 'bold',
          borderBottom: '3px solid var(--primary-color, #261f87)',
          paddingBottom: '10px'
        }}>
          Para Clientes Nuevos
        </h2>

        {/* Video Explicativo del Servicio */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <VideoExplicativo videoId="81dZQyVEaio" titulo="Ver video explicativo" />
        </div>

        {/* Instructivo para Activar Trading Automatizado */}
        <div className="text-center" style={{ marginBottom: '30px', color: 'var(--text-color, #261f87)' }}>
          <h3 style={{ marginBottom: '10px' }}>Instructivo para Activar Trading Automatizado</h3>
          <p style={{ opacity: 0.8 }}>
            Mira nuestros videos tutoriales para descubrir cómo activar el Trading Automatizado.
          </p>
        </div>

        <div className="tutorials-section" style={{ marginBottom: '30px' }}>
          <Accordion items={TUTORIAL_VIDEOS} />
        </div>

        {/* Botón Subir Código QR */}
        <div style={{ textAlign: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowQrWindow(true)}
            disabled={isLoading}
            style={{ padding: '12px 24px', fontSize: '1rem' }}
          >
            Subir Código QR
          </button>
        </div>
      </div>

      {/* ========== SECCIÓN 2: PARA CLIENTES ACTIVOS ========== */}
      <div className="seccion-clientes-activos" style={{
        borderTop: '1px solid rgba(255,255,255,0.1)',
        paddingTop: '30px'
      }}>
        <h2 style={{
          marginBottom: '30px',
          color: 'var(--text-color, #261f87)',
          fontSize: '1.8rem',
          fontWeight: 'bold',
          borderBottom: '3px solid var(--primary-color, #261f87)',
          paddingBottom: '10px'
        }}>
          Para Clientes Activos
        </h2>

        {/* --- NUEVO: DISPLAY DE SALDO --- */}
        {/* --- NUEVO: DISPLAY DE SALDO (Estilo Dashboard - Fuentes Negras) --- */}
        <div style={{ margin: '20px auto', maxWidth: '400px' }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.8)', padding: '20px', borderRadius: '8px', borderLeft: '4px solid #000', textAlign: 'left' }}>
            <div style={{ fontSize: '0.85rem', color: '#000', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '5px', fontWeight: 'bold' }}>Binance Futures</div>
            {loadingBalance ? (
              <div style={{ fontSize: '1.2rem', color: '#000' }}>Cargando...</div>
            ) : balanceData ? (
              <>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#000' }}>
                  {balanceData.balance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#000', fontWeight: '500' }}>{balanceData.currency} Margin Balance</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '1.2rem', color: '#000', fontStyle: 'italic' }}>--.--</div>
                <div style={{ fontSize: '0.8rem', color: '#d32f2f', marginTop: '5px', fontWeight: 'bold' }}>
                  Cuenta no vinculada. <br />
                  <span style={{ color: '#333' }}>Contacte a soporte.</span>
                </div>
              </>
            )}
          </div>
        </div>
        {/* ------------------------------- */}
        {/* ------------------------------- */}

        {error && <p style={{ color: 'red', marginBottom: '15px', textAlign: 'center' }}>{error}</p>}

        <div style={{ textAlign: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={handleSolicitarInforme}
            disabled={isLoading}
            style={{ padding: '12px 24px', fontSize: '1rem' }}
          >
            {isLoading ? 'Procesando...' : 'Solicitar Informe'}
          </button>
        </div>
      </div>

      {/* --- RENDERIZADO DEL MODAL  --- */}
      {showQrWindow && (
        <UploadQRModal

          currentUserData={userData || {}}

          // 2. Pasamos la función de cierre
          onClose={() => setShowQrWindow(false)}

          // 3. Pasamos la función para guardar (que definimos arriba)
          onSave={handleSaveQr}

          // 4. Pasamos el estado de carga
          isLoading={isLoading}
        />
      )}

    </div>
  );
};

export default TradingAutomatizadoView;